// src/views/ShoppingIntegrations.jsx
import React, { useEffect, useMemo, useState } from 'react';
import './ShoppingIntegrations.css';

// Action icons (match Receipts sizing)
import { FaPlus, FaEdit, FaTrash, FaEye, FaStore, FaPlug, FaChevronRight, FaChevronLeft, FaCheck, FaTimes, FaRegSave, FaKey } from 'react-icons/fa';
import { SiShopify, SiAmazon, SiEbay, SiWalmart, SiWoocommerce } from 'react-icons/si';

const API = import.meta.env.VITE_API_URL;
const API_ROOT = API ? API.replace(/\/$/, '') : '';

// Map servicetype -> icon component
function BrandIcon({ type }) {
  const t = String(type || '').toLowerCase();
  if (t.includes('shopify'))     return <SiShopify className="integration-icon shopify" title="Shopify" />;
  if (t.includes('amazon'))      return <SiAmazon  className="integration-icon amazon" title="Amazon" />;
  if (t.includes('ebay'))        return <SiEbay    className="integration-icon ebay" title="eBay" />;
  if (t.includes('walmart'))     return <SiWalmart className="integration-icon walmart" title="Walmart" />;
  if (t.includes('woo'))         return <SiWoocommerce className="integration-icon woocommerce" title="WooCommerce" />;
  return <FaStore className="integration-icon generic" title="Integration" />;
}

const SERVICE_OPTIONS = ['Shopify', 'eBay', 'Amazon', 'Walmart', 'WooCommerce'];

function openShopifyOauthWindow(targetUrl) {
  if (!targetUrl) return false;
  const width = 900;
  const height = 720;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');

  let popup = null;
  try {
    popup = window.open(targetUrl, '_blank', features);
  } catch {
    popup = null;
  }

  if (popup) {
    try { popup.opener = null; } catch {}
    popup.focus?.();
    return true;
  }
  return false;
}

function startShopifyOauthFlow(targetUrl) {
  const opened = openShopifyOauthWindow(targetUrl);
  if (opened) return;
  alert('Your browser blocked the popup window. We will open Shopify authorization in this tab instead. Use your back button to return to Easy3PL after completing authorization.');
  window.location.href = targetUrl;
}

// LocalStorage key for sort persistence
const SORT_KEY = 'shoppingintegrations.sort.v1';
function getInitialSort() {
  try {
    const raw = localStorage.getItem(SORT_KEY);
    if (!raw) return { sortField: 'integration', sortOrder: 'ASC' };
    const j = JSON.parse(raw);
    return {
      sortField: j?.sortField || 'integration',
      sortOrder: j?.sortOrder === 'DESC' ? 'DESC' : 'ASC',
    };
  } catch {
    return { sortField: 'integration', sortOrder: 'ASC' };
  }
}

function parseShopifyOauthMeta(val) {
  if (!val) return null;
  try {
    const parsed = typeof val === 'string' ? JSON.parse(val) : val;
    if (!parsed) return null;
    if (parsed.type === 'shopify_oauth') return parsed;
    if (parsed.shopify_oauth && parsed.shopify_oauth.type === 'shopify_oauth') return parsed.shopify_oauth;
    return null;
  } catch {
    return null;
  }
}

/* =================== Create/Edit Modal (existing) =================== */
function IntegrationModal({ open, onClose, onSave, initial, clients = [] }) {
  const isEdit = !!initial?.id;
  const [servicetype, setServicetype] = useState(initial?.servicetype || 'Shopify');
  const [name, setName] = useState(initial?.name || '');
  const [displayname, setDisplayname] = useState(initial?.displayname || '');
  const [clientid, setClientid] = useState(initial?.clientid != null ? String(initial.clientid) : '');
  const [clientname, setClientname] = useState(initial?.clientname || '');
  const [storeid, setStoreid] = useState(initial?.storeid != null ? String(initial.storeid) : '');
  const [enabled, setEnabled] = useState(!!initial?.enabled || !isEdit);
  const [fulfillment_location_id, setFulfillmentLocationId] = useState(
    initial?.fulfillment_location_id != null ? String(initial.fulfillment_location_id) : ''
  );
  // Import Closed Orders (0/1)
  const [import_closed_orders, setImportClosedOrders] = useState(
    initial?.import_closed_orders ? 1 : 0
  );

  // Amazon (SP-API) credential fields (saved into field1..field5)
  const [amzLwaClientId, setAmzLwaClientId] = useState('');
  const [amzLwaClientSecret, setAmzLwaClientSecret] = useState('');
  const [amzLwaRefreshToken, setAmzLwaRefreshToken] = useState('');
  const [amzSellerId, setAmzSellerId] = useState('');
  const [amzRegion, setAmzRegion] = useState('na');
  const [amzMarketplaceIds, setAmzMarketplaceIds] = useState('');
  const [amzRoleArn, setAmzRoleArn] = useState('');
  const [amzAwsAccessKeyId, setAmzAwsAccessKeyId] = useState('');
  const [amzAwsSecretAccessKey, setAmzAwsSecretAccessKey] = useState('');

  const [shopifyAdminToken, setShopifyAdminToken] = useState(initial?.field1 || '');
  const [shopifyApiKey, setShopifyApiKey] = useState(initial?.field2 || '');
  const [shopifyApiSecret, setShopifyApiSecret] = useState(initial?.field3 || '');
  const [shopifyMeta, setShopifyMeta] = useState(() => parseShopifyOauthMeta(initial?.field5));

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setServicetype(initial?.servicetype || 'Shopify');
    setName(initial?.name || '');
    setDisplayname(initial?.displayname || '');
    setClientid(initial?.clientid != null ? String(initial.clientid) : '');
    setClientname(initial?.clientname || '');
    setStoreid(initial?.storeid != null ? String(initial.storeid) : '');
    setEnabled(!!initial?.enabled || !isEdit);
    setFulfillmentLocationId(initial?.fulfillment_location_id != null ? String(initial.fulfillment_location_id) : '');
    setImportClosedOrders(initial?.import_closed_orders ? 1 : 0); // reset on open
    setShopifyAdminToken(initial?.field1 || '');
    setShopifyApiKey(initial?.field2 || '');
    setShopifyApiSecret(initial?.field3 || '');
    setShopifyMeta(parseShopifyOauthMeta(initial?.field5));
    setErrors({});

    // Prefill Amazon credentials when editing
    try {
      const svc = String(initial?.servicetype || '').toLowerCase();
      if (svc.includes('amazon')) {
        setAmzLwaClientId(initial?.field1 || '');
        setAmzLwaClientSecret(initial?.field2 || '');
        setAmzLwaRefreshToken(initial?.field3 || '');
        setAmzSellerId(initial?.field4 || '');
        const extra = initial?.field5 ? JSON.parse(initial.field5) : {};
        setAmzRegion(extra?.region || 'na');
        const csv = Array.isArray(extra?.marketplaceIds) ? extra.marketplaceIds.join(',') : (extra?.marketplaceIds || '');
        setAmzMarketplaceIds(csv);
        setAmzRoleArn(extra?.roleArn || '');
        setAmzAwsAccessKeyId(extra?.awsAccessKeyId || '');
        setAmzAwsSecretAccessKey(extra?.awsSecretAccessKey || '');
      } else {
        setAmzLwaClientId(''); setAmzLwaClientSecret(''); setAmzLwaRefreshToken('');
        setAmzSellerId(''); setAmzRegion('na'); setAmzMarketplaceIds('');
        setAmzRoleArn(''); setAmzAwsAccessKeyId(''); setAmzAwsSecretAccessKey('');
      }
    } catch {
      setAmzRegion('na'); setAmzMarketplaceIds('');
    }
  }, [open, initial, isEdit]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = '';//meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Shopping Integrations', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [] );

  if (!open) return null;

  const newSvcType = servicetype || 'Shopify';
  const svc = String(newSvcType || '').toLowerCase();
  const isShopify = svc.includes('shopify');
  const shopifyOauthConnected = isShopify && !!shopifyMeta;
  const handleShopifyOAuth = () => {
    if (!isEdit || !initial?.id) {
      alert('Save the integration before starting the Shopify OAuth flow.');
      return;
    }
    if (!shopifyApiKey.trim() || !shopifyApiSecret.trim()) {
      alert('Enter your Shopify API key and secret first.');
      return;
    }
    const urlBase = API_ROOT || '';
    const target = `${urlBase}/api/shopify/integration/${initial.id}/oauth/start`;
    startShopifyOauthFlow(target);
  };
  const getHelp = (field) => {
    switch (field) {
      case 'servicetype':
        return 'Choose the marketplace/platform to connect (Shopify, Amazon, eBay, Walmart, WooCommerce).';
      case 'name':
        if (svc.includes('shopify')) return 'Domain prefix only. Example: "mybrand" → mybrand.myshopify.com';
        if (svc.includes('amazon')) return 'Internal reference name for this Seller account/profile.';
        if (svc.includes('ebay')) return 'Your eBay store or seller account name (for reference).';
        if (svc.includes('walmart')) return 'Your Walmart Partner/Store identifier (for reference).';
        if (svc.includes('woo')) return 'WooCommerce site identifier (host or slug for reference).';
        return 'Short identifier for this integration (used internally).';
      case 'displayname':
        return 'Human‑friendly label shown in the UI for this integration.';
      case 'storeid':
        if (svc.includes('shopify')) return 'Optional numeric Store ID (legacy/advanced). Can be left blank.';
        if (svc.includes('amazon')) return 'Not used for Amazon SP‑API. Leave blank.';
        return 'Optional store identifier. May not apply for all service types.';
      case 'client':
        return 'Client that owns this integration and receives imported orders.';
      case 'fulfillment_location_id':
        if (svc.includes('shopify')) return 'Shopify Fulfillment Location ID used when creating fulfillments.';
        if (svc.includes('amazon')) return 'Not required for Amazon SP‑API shipping. Leave blank unless instructed.';
        return 'Fulfillment location identifier (if applicable for the service).';
      case 'import_closed_orders':
        return 'When enabled, import closed/fulfilled orders in addition to open ones (may increase import time).';
      default:
        return '';
    }
  };
  const getPlaceholder = (field) => {
    switch (field) {
      case 'name':
        return svc.includes('shopify') ? 'mybrand' : svc.includes('amazon') ? 'Seller profile name' : 'Integration key';
      case 'displayname':
        return 'Shown in UI (e.g., "Brand US Amazon")';
      case 'storeid':
        return svc.includes('shopify') ? 'Optional' : 'Leave blank if not applicable';
      case 'fulfillment_location_id':
        return svc.includes('shopify') ? 'Shopify Location ID (e.g., 123456789)' : 'Leave blank if not applicable';
      default:
        return undefined;
    }
  };

  const validate = () => {
    const e = {};
    if (!servicetype) e.servicetype = 'Service is required';
    if (!name.trim()) e.name = 'Name is required';
    if (!clientid) e.clientid = 'Client is required';
    if (svc.includes('shopify')) {
      const hasLegacyToken = shopifyAdminToken.trim().length > 0;
      const hasOAuthCreds = shopifyApiKey.trim().length > 0 && shopifyApiSecret.trim().length > 0;
      if (!hasLegacyToken) {
        if (!shopifyApiKey.trim()) e.shopifyApiKey = 'API Key is required for Shopify OAuth.';
        if (!shopifyApiSecret.trim()) e.shopifyApiSecret = 'API Secret is required for Shopify OAuth.';
        if (!shopifyApiKey.trim() || !shopifyApiSecret.trim()) {
          // fall through to generic message below if both missing
        }
      }
      if (!hasLegacyToken && !hasOAuthCreds && !e.shopifyApiKey) {
        e.shopifyApiKey = 'Provide Shopify API credentials or a legacy Admin API token.';
      }
    }
    // Amazon-specific required fields
    if (svc.includes('amazon')) {
      if (!amzLwaClientId.trim()) e.amzLwaClientId = 'LWA Client ID is required';
      if (!amzLwaClientSecret.trim()) e.amzLwaClientSecret = 'LWA Client Secret is required';
      if (!amzLwaRefreshToken.trim()) e.amzLwaRefreshToken = 'LWA Refresh Token is required';
      if (!amzSellerId.trim()) e.amzSellerId = 'Seller ID is required';
      if (!amzRegion.trim()) e.amzRegion = 'Region is required';
      const mps = (amzMarketplaceIds || '').split(',').map(s => s.trim()).filter(Boolean);
      if (!mps.length) e.amzMarketplaceIds = 'At least one Marketplace ID is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const doSave = () => {
    if (!validate()) return;
    const payload = {
      id: initial?.id ?? null,
      servicetype,
      name: name.trim(),
      displayname: (displayname || name).trim(),
      clientid: clientid ? Number(clientid) : 0,
      clientname: clientname || null,
      storeid: storeid ? Number(storeid) : 0,
      enabled: enabled ? 1 : 0,
      fulfillment_location_id: fulfillment_location_id ? String(fulfillment_location_id) : '0',
      import_closed_orders: Number(import_closed_orders) || 0,
    };

    const svcName = String(servicetype || '').toLowerCase();
    if (svcName.includes('amazon')) {
      payload.field1 = amzLwaClientId || null;
      payload.field2 = amzLwaClientSecret || null;
      payload.field3 = amzLwaRefreshToken || null;
      payload.field4 = amzSellerId || null;
      const extra = {
        region: amzRegion || 'na',
        marketplaceIds: (amzMarketplaceIds || '').split(',').map(s => s.trim()).filter(Boolean),
        roleArn: amzRoleArn || undefined,
        awsAccessKeyId: amzAwsAccessKeyId || undefined,
        awsSecretAccessKey: amzAwsSecretAccessKey || undefined,
      };
      payload.field5 = JSON.stringify(extra);
    }

    if (svcName.includes('shopify')) {
      payload.field1 = shopifyAdminToken?.trim() ? shopifyAdminToken.trim() : null;
      payload.field2 = shopifyApiKey?.trim() ? shopifyApiKey.trim() : null;
      payload.field3 = shopifyApiSecret?.trim() ? shopifyApiSecret.trim() : null;
    }

    onSave(payload);
  };

  return (
    <div className="si-modal-overlay">
      <div className="si-modal" onClick={(e) => e.stopPropagation()}>
        <div className="si-modal-titlebar">
          <h3 className="si-modal-title">{isEdit ? 'Edit Integration' : 'Add Integration'}</h3>
          <button className="icon-btn" aria-label="Close" onClick={onClose}>×</button>
        </div>

        <div className="si-form-grid">
          <label>
            <span className="si-label-row">Service Type <span className="si-help" title={getHelp('servicetype')}>?</span></span>
            <select
              value={servicetype}
              onChange={(e) => setServicetype(e.target.value)}
              className={errors.servicetype ? 'field-error' : undefined}
            >
              {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.servicetype && <span className="si-error">{errors.servicetype}</span>}
          </label>

          <label>
            <span className="si-label-row">Enabled <span className="si-help" title="Turn off to temporarily disable imports/actions for this integration.">?</span></span>
            <select
              value={enabled ? 1 : 0}
              onChange={e => setEnabled(Number(e.target.value) === 1)}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </label>

          <label>
            <span className="si-label-row">Name <span className="si-help" title={getHelp('name')}>?</span></span>
            <input
              placeholder={getPlaceholder('name')}
              value={name}
              onChange={e => setName(e.target.value)}
              className={errors.name ? 'field-error' : undefined}
            />
            {errors.name && <span className="si-error">{errors.name}</span>}
          </label>

          <label>
            <span className="si-label-row">Display Name <span className="si-help" title={getHelp('displayname')}>?</span></span>
            <input
              placeholder={getPlaceholder('displayname')}
              value={displayname}
              onChange={e => setDisplayname(e.target.value)}
            />
          </label>

          <label>
            <span className="si-label-row">Store ID <span className="si-help" title={getHelp('storeid')}>?</span></span>
            <input
              type="number"
              inputMode="numeric"
              placeholder={getPlaceholder('storeid')}
              value={storeid}
              onChange={e => setStoreid(e.target.value)}
            />
          </label>

          <label className="si-span-2">
            <span className="si-label-row">Client <span className="si-help" title={getHelp('client')}>?</span></span>
            <select
              value={clientid}
              onChange={e => {
                const val = e.target.value;
                setClientid(val);
                const found = clients.find(c => String(c.id) === String(val));
                setClientname(found?.name || '');
              }}
              className={errors.clientid ? 'field-error' : undefined}
            >
              <option value="">— Select Client —</option>
              {clients.map(c => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
            {errors.clientid && <span className="si-error">{errors.clientid}</span>}
          </label>

          <label className="si-span-2">
            <span className="si-label-row">Fulfillment Location ID <span className="si-help" title={getHelp('fulfillment_location_id')}>?</span></span>
            <input
              type="text"
              placeholder={getPlaceholder('fulfillment_location_id')}
              value={fulfillment_location_id}
              onChange={e => setFulfillmentLocationId(e.target.value)}
            />
          </label>

          {isShopify && (
            <>
              <label className="si-span-2">
                <span className="si-label-row">Legacy Admin API Token <span className="si-help" title="Optional legacy Admin API token (shpat_…). Provide only if you still manage a private app token.">?</span></span>
                <input
                  type="password"
                  placeholder="shpat_… (optional)"
                  value={shopifyAdminToken}
                  onChange={e => setShopifyAdminToken(e.target.value)}
                />
              </label>

              <label className="si-span-2">
                <span className="si-label-row">Shopify API Key (Client ID) <span className="si-help" title="Found in the Shopify Dev / Partner Dashboard under App Setup. Required for OAuth installs.">?</span></span>
                <input
                  placeholder="e.g., 1234567890abcdef"
                  value={shopifyApiKey}
                  onChange={e => setShopifyApiKey(e.target.value)}
                  className={errors.shopifyApiKey ? 'field-error' : undefined}
                />
                {errors.shopifyApiKey && <span className="si-error">{errors.shopifyApiKey}</span>}
              </label>

              <label className="si-span-2">
                <span className="si-label-row">Shopify API Secret <span className="si-help" title="Client secret from the Shopify Dev / Partner Dashboard. Required for OAuth installs.">?</span></span>
                <input
                  type="password"
                  placeholder="App secret"
                  value={shopifyApiSecret}
                  onChange={e => setShopifyApiSecret(e.target.value)}
                  className={errors.shopifyApiSecret ? 'field-error' : undefined}
                />
                {errors.shopifyApiSecret && <span className="si-error">{errors.shopifyApiSecret}</span>}
              </label>

              <div className="si-span-2 si-oauth-hint">
                <div className={`si-status-pill ${shopifyOauthConnected ? 'success' : 'muted'}`}>
                  {shopifyOauthConnected ? 'OAuth Connected' : 'OAuth Not Connected'}
                </div>
                {shopifyMeta?.scope && (
                  <div className="si-oauth-detail">Scopes: {shopifyMeta.scope}</div>
                )}
                {shopifyMeta?.installedAt && (
                  <div className="si-oauth-detail">
                    Granted: {new Date(shopifyMeta.installedAt).toLocaleString()}
                  </div>
                )}
                {!shopifyOauthConnected && shopifyAdminToken.trim() && (
                  <div className="si-oauth-detail">Legacy Admin API token present.</div>
                )}
                {isEdit ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={handleShopifyOAuth}
                    disabled={!shopifyApiKey.trim() || !shopifyApiSecret.trim()}
                  >
                    {shopifyOauthConnected ? 'Reconnect Shopify OAuth' : 'Connect via Shopify OAuth'}
                  </button>
                ) : (
                  <div className="si-oauth-detail">Save the integration to launch Shopify OAuth.</div>
                )}
              </div>
            </>
          )}

          <label>
            <span className="si-label-row">Import Closed Orders <span className="si-help" title={getHelp('import_closed_orders')}>?</span></span>
            <select
              value={import_closed_orders}
              onChange={e => setImportClosedOrders(Number(e.target.value))}
            >
              <option value={0}>No</option>
              <option value={1}>Yes</option>
            </select>
          </label>
        </div>

        {/* Amazon credential fields (shown only when Amazon service selected) */}
        {svc.includes('amazon') && (
          <div className="si-form-grid" style={{ marginTop: 12 }}>
            <div className="si-span-2" style={{ fontWeight: 600 }}>Amazon (SP‑API) Credentials</div>

            <label>
              <span className="si-label-row">LWA Client ID <span className="si-help" title="Login With Amazon client identifier for your SP‑API app.">?</span></span>
              <input value={amzLwaClientId} onChange={e => setAmzLwaClientId(e.target.value)} placeholder="amzn1.application-oa2-client…" />
            </label>

            <label>
              <span className="si-label-row">LWA Client Secret <span className="si-help" title="Secret for the LWA client. Keep secure.">?</span></span>
              <input type="password" value={amzLwaClientSecret} onChange={e => setAmzLwaClientSecret(e.target.value)} placeholder="••••••••" />
            </label>

            <label className="si-span-2">
              <span className="si-label-row">LWA Refresh Token <span className="si-help" title="Seller authorization refresh token obtained after consent.">?</span></span>
              <input type="password" value={amzLwaRefreshToken} onChange={e => setAmzLwaRefreshToken(e.target.value)} placeholder="Atzr|…" />
            </label>

            <label>
              <span className="si-label-row">Seller ID <span className="si-help" title="Amazon Merchant/Seller ID for this account.">?</span></span>
              <input value={amzSellerId} onChange={e => setAmzSellerId(e.target.value)} placeholder="A1ABCDEF…" />
            </label>

            <label>
              <span className="si-label-row">Region <span className="si-help" title="SP‑API region: na (North America), eu (Europe), fe (Far East).">?</span></span>
              <select value={amzRegion} onChange={e => setAmzRegion(e.target.value)}>
                <option value="na">na (North America)</option>
                <option value="eu">eu (Europe)</option>
                <option value="fe">fe (Far East)</option>
              </select>
            </label>

            <label className="si-span-2">
              <span className="si-label-row">Marketplace IDs <span className="si-help" title="Comma‑separated marketplace IDs (e.g., ATVPDKIKX0DER for US).">?</span></span>
              <input value={amzMarketplaceIds} onChange={e => setAmzMarketplaceIds(e.target.value)} placeholder="ATVPDKIKX0DER, A1F83G8C2ARO7P" />
            </label>

            <label className="si-span-2">
              <span className="si-label-row">Role ARN (preferred) <span className="si-help" title="IAM role ARN authorized for your SP‑API app. If provided, static keys below are optional.">?</span></span>
              <input value={amzRoleArn} onChange={e => setAmzRoleArn(e.target.value)} placeholder="arn:aws:iam::123456789012:role/SpApiRole" />
            </label>

            <label>
              <span className="si-label-row">AWS Access Key ID <span className="si-help" title="Optional fallback. Use role ARN instead when possible.">?</span></span>
              <input value={amzAwsAccessKeyId} onChange={e => setAmzAwsAccessKeyId(e.target.value)} placeholder="AKIA…" />
            </label>

            <label>
              <span className="si-label-row">AWS Secret Access Key <span className="si-help" title="Optional fallback secret. Keep secure.">?</span></span>
              <input type="password" value={amzAwsSecretAccessKey} onChange={e => setAmzAwsSecretAccessKey(e.target.value)} placeholder="••••••••" />
            </label>
          </div>
        )}

        <div className="si-modal-actions">
          <button className="btn" onClick={onClose}><FaTimes /> Cancel</button>
          <button className="btn primary" onClick={doSave}>
            <FaRegSave /> {isEdit ? 'Save Changes' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(value) {
  if (!value && value !== 0) return '';
  const dt = new Date(value);
  if (!Number.isNaN(dt.getTime())) {
    return dt.toLocaleString();
  }
  if (typeof value === 'string' && !value.trim()) return '';
  return value ?? '';
}

function IntegrationDetailsModal({ open, row, onClose }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open, row]);

  if (!open || !row) return null;

  const svc = String(row?.servicetype || '').toLowerCase();
  const detailItems = [
    { label: 'Client', value: row.clientname || (row.clientid ? `#${row.clientid}` : '') },
    { label: 'Integration Name', value: row.name },
    { label: 'Display Name', value: row.displayname },
    { label: 'Service Type', value: row.servicetype },
    { label: 'Enabled', value: !!row.enabled ? 'Yes' : 'No' },
    { label: 'Store ID', value: row.storeid },
    { label: 'Fulfillment Location ID', value: row.fulfillment_location_id },
    { label: 'Import Closed Orders', value: Number(row.import_closed_orders) ? 'Yes' : 'No' },
    { label: 'Created', value: formatDateTime(row.created_at ?? row.created ?? row.createdon ?? row.created_on) },
    { label: 'Updated', value: formatDateTime(row.updated_at ?? row.updated ?? row.updatedon ?? row.updated_on) },
  ];

  const parsedField5 = (() => {
    if (!row?.field5) return null;
    try {
      return typeof row.field5 === 'string' ? JSON.parse(row.field5) : row.field5;
    } catch {
      return row.field5;
    }
  })();
  const shopifyOauthMeta = svc.includes('shopify') ? parseShopifyOauthMeta(row.field5) : null;

  const knownCreds = [];
  if (svc.includes('shopify')) {
    if (row.field1) knownCreds.push({ label: 'Admin API Token', value: row.field1 });
  } else if (svc.includes('amazon')) {
    if (row.field1) knownCreds.push({ label: 'LWA Client ID', value: row.field1 });
    if (row.field2) knownCreds.push({ label: 'LWA Client Secret', value: row.field2 });
    if (row.field3) knownCreds.push({ label: 'LWA Refresh Token', value: row.field3 });
    if (row.field4) knownCreds.push({ label: 'Seller ID', value: row.field4 });
    if (parsedField5 && typeof parsedField5 === 'object') {
      if (parsedField5.region) knownCreds.push({ label: 'Region', value: parsedField5.region });
      if (parsedField5.marketplaceIds) {
        const ids = Array.isArray(parsedField5.marketplaceIds) ? parsedField5.marketplaceIds.join(', ') : parsedField5.marketplaceIds;
        knownCreds.push({ label: 'Marketplace IDs', value: ids });
      }
      if (parsedField5.roleArn) knownCreds.push({ label: 'Role ARN', value: parsedField5.roleArn });
      if (parsedField5.awsAccessKeyId) knownCreds.push({ label: 'AWS Access Key ID', value: parsedField5.awsAccessKeyId });
      if (parsedField5.awsSecretAccessKey) knownCreds.push({ label: 'AWS Secret Access Key', value: parsedField5.awsSecretAccessKey });
    } else if (row.field5) {
      knownCreds.push({ label: 'Extra (field5)', value: row.field5 });
    }
  }

  const genericCreds = [];
  if (!knownCreds.length) {
    for (let i = 1; i <= 6; i += 1) {
      const key = `field${i}`;
      const val = row?.[key];
      if (val != null && val !== '') {
        genericCreds.push({ label: key, value: typeof val === 'string' ? val : JSON.stringify(val) });
      }
    }
  }

  const metaValue = (() => {
    const raw = row.meta ?? row.metadata ?? row.extra ?? null;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    }
    return raw;
  })();

  const rawJson = JSON.stringify(row, null, 2);

  const copyJson = async () => {
    if (!rawJson) return;
    try {
      if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(rawJson);
      } else {
        throw new Error('clipboard not available');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      try {
        if (typeof window !== 'undefined') {
          window.prompt('Copy JSON', rawJson);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }
      } catch {
        // ignore
      }
    }
  };

  const renderValue = (val) => {
    if (val === null || val === undefined || val === '') return <span className="si-muted">—</span>;
    return val;
  };

  return (
    <div className="si-modal-overlay" onClick={onClose}>
      <div className="si-modal" onClick={(e) => e.stopPropagation()}>
        <div className="si-modal-titlebar">
          <h3 className="si-modal-title">Integration Details</h3>
          <button className="icon-btn" aria-label="Close" onClick={onClose}>×</button>
        </div>

        <div className="si-detail-grid">
          {detailItems.map(item => (
            <div key={item.label} className="si-detail-item">
              <div className="si-detail-label">{item.label}</div>
              <div className="si-detail-value">{renderValue(item.value)}</div>
            </div>
          ))}
        </div>

        {(knownCreds.length > 0 || genericCreds.length > 0) && (
          <>
            <div className="si-section-title">Credentials / Extra Fields</div>
            <div className="si-detail-grid">
              {(knownCreds.length ? knownCreds : genericCreds).map(item => (
                <div key={item.label} className="si-detail-item">
                  <div className="si-detail-label">{item.label}</div>
                  <div className="si-detail-value">{renderValue(item.value)}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {shopifyOauthMeta && (
          <>
            <div className="si-section-title">Shopify OAuth</div>
            <div className="si-detail-grid">
              <div className="si-detail-item">
                <div className="si-detail-label">Status</div>
                <div className="si-detail-value">{shopifyOauthMeta ? 'Connected' : 'Not connected'}</div>
              </div>
              <div className="si-detail-item">
                <div className="si-detail-label">Scopes</div>
                <div className="si-detail-value">{renderValue(shopifyOauthMeta.scope)}</div>
              </div>
              <div className="si-detail-item">
                <div className="si-detail-label">Shop</div>
                <div className="si-detail-value">{renderValue(shopifyOauthMeta.shop)}</div>
              </div>
              <div className="si-detail-item">
                <div className="si-detail-label">Granted</div>
                <div className="si-detail-value">
                  {shopifyOauthMeta.installedAt ? new Date(shopifyOauthMeta.installedAt).toLocaleString() : '—'}
                </div>
              </div>
            </div>
          </>
        )}

        {metaValue && (
          <>
            <div className="si-section-title">Metadata</div>
            <div className="si-prewrap">
              <pre>{typeof metaValue === 'string' ? metaValue : JSON.stringify(metaValue, null, 2)}</pre>
            </div>
          </>
        )}

        <div className="si-section-title">Raw Record</div>
        <div className="si-prewrap">
          <pre>{rawJson}</pre>
        </div>

        <div className="si-modal-actions">
          <button className="btn" onClick={copyJson}>{copied ? 'Copied!' : 'Copy JSON'}</button>
          <button className="btn primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* =================== NEW STORE WIZARD (Shopify) =================== */
function NewStoreWizard({ open, onClose, onCreated, clients = [] }) {
  const [step, setStep] = useState(1);
  const [platform, setPlatform] = useState('Shopify'); // hardcoded
  const [displayname, setDisplayname] = useState('');
  const [domainPrefix, setDomainPrefix] = useState(''); // the "mybrand" part
  const [adminToken, setAdminToken] = useState('');     // Shopify Admin API access token
  const [shopifyApiKey, setShopifyApiKey] = useState('');
  const [shopifyApiSecret, setShopifyApiSecret] = useState('');
  const [storeId, setStoreId] = useState('');
  const [fulfillmentLocationId, setFulfillmentLocationId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // client selection
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    if (!open) return;
    // reset when opening
    setStep(1);
    setPlatform('Shopify');
    setDisplayname('');
    setDomainPrefix('');
    setAdminToken('');
    setShopifyApiKey('');
    setShopifyApiSecret('');
    setStoreId('');
    setFulfillmentLocationId('');
    setEnabled(true);
    setSaving(false);
    setError('');
    setClientId('');
    setClientName('');
  }, [open]);

  if (!open) return null;

  const svc = String(platform || 'Shopify').toLowerCase();
  const canNextFrom1 = platform === 'Shopify';
  const hasLegacyToken = adminToken.trim().length > 0;
  const hasOauthCreds = shopifyApiKey.trim().length > 0 && shopifyApiSecret.trim().length > 0;
  const canNextFrom2 = domainPrefix.trim().length > 0 && (hasLegacyToken || hasOauthCreds);
  const canSave      = canNextFrom2 && !!clientId; // require client

  const saveStore = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        servicetype: 'Shopify',
        name: domainPrefix.trim(),
        displayname: (displayname || domainPrefix).trim(),
        enabled: enabled ? 1 : 0,
        storeid: storeId ? Number(storeId) : 0,
        fulfillment_location_id: fulfillmentLocationId || '',
        field1: adminToken.trim() ? adminToken.trim() : null,
        field2: shopifyApiKey.trim() ? shopifyApiKey.trim() : null,
        field3: shopifyApiSecret.trim() ? shopifyApiSecret.trim() : null,
        clientid: Number(clientId),
        clientname: clientName || null,
        import_closed_orders: 0, // default for wizard; user can edit later
      };
      const res = await fetch(`${API}/api/shoppingintegrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || 'Create failed');

      try {
        const test = await fetch(`${API}/api/shoppingintegrations/${j.id}/test`);
        const td = await test.json();
        if (!test.ok) {
          console.warn('Test failed:', td);
        }
      } catch (e) {
        console.warn('Test threw:', e);
      }

      onCreated?.();
      onClose?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const StepHeader = ({ children }) => (
    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '1rem' }}>{children}</div>
  );

  const StepActions = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
      <div>
        {step > 1 && (
          <button className="btn" onClick={() => setStep(s => s - 1)}>
            <FaChevronLeft /> Back
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onClose}><FaTimes /> Cancel</button>
        {step < 3 && (
          <button
            className="btn primary"
            onClick={() => setStep(s => s + 1)}
            disabled={(step === 1 && !canNextFrom1) || (step === 2 && !canNextFrom2)}
          >
            Next <FaChevronRight />
          </button>
        )}
        {step === 3 && (
          <button
            className="btn primary"
            onClick={saveStore}
            disabled={!canSave || saving}
            title={!canSave ? 'Select a client and fill required fields' : undefined}
          >
            {saving ? 'Saving…' : (<>Finish <FaCheck /></>)}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="si-modal-overlay">
      <div className="si-modal" onClick={(e) => e.stopPropagation()}>
        <div className="si-modal-titlebar">
          <h3 className="si-modal-title">New Store Setup</h3>
          <button className="icon-btn" aria-label="Close" onClick={onClose}>×</button>
        </div>

        {step === 1 && (
          <div>
            <StepHeader>Select a Store Platform</StepHeader>
            <div className="si-form-grid" style={{ gridTemplateColumns: '1fr' }}>
              <label>
                <span className="si-label-row">Platform <span className="si-help" title="Currently supports Shopify quick‑setup. Use Advanced for other services.">?</span></span>
                <select value={platform} onChange={e => setPlatform(e.target.value)}>
                  <option value="Shopify">Shopify</option>
                </select>
              </label>

              <div style={{ marginTop: 6, fontSize: 13, opacity: .85 }}>
                We currently support Shopify in this wizard. More platforms coming soon.
              </div>
            </div>

            <StepActions />
          </div>
        )}

        {step === 2 && (
          <div>
            <StepHeader>Shopify – Store Basics</StepHeader>
            <div className="si-form-grid">
              <label>
                <span className="si-label-row">Display Name (shown in UI) <span className="si-help" title="Human‑friendly label shown in the list of integrations.">?</span></span>
                <input
                  placeholder="e.g., Acme Direct Store"
                  value={displayname}
                  onChange={e => setDisplayname(e.target.value)}
                />
              </label>

              <label>
                <span className="si-label-row">Enabled <span className="si-help" title="Turn off to temporarily disable imports/actions for this integration.">?</span></span>
                <select value={enabled ? 1 : 0} onChange={e => setEnabled(Number(e.target.value) === 1)}>
                  <option value={1}>Yes</option>
                  <option value={0}>No</option>
                </select>
              </label>

              <label className="si-span-2">
                <span className="si-label-row">Shopify Domain Prefix <span className="si-help" title='Domain prefix only. Example: "mybrand" → mybrand.myshopify.com'>?</span></span>
                <input
                  placeholder='Only the prefix, e.g., "mybrand" → mybrand.myshopify.com'
                  value={domainPrefix}
                  onChange={e => setDomainPrefix(e.target.value)}
                />
              </label>

              <label className="si-span-2">
                <span className="si-label-row">Admin API Access Token <span className="si-help" title='Shopify Admin API access token with required scopes (read_orders, write_fulfillments, etc.)'>?</span></span>
                <input
                  type="password"
                  placeholder="shpat_…"
                  value={adminToken}
                  onChange={e => setAdminToken(e.target.value)}
                />
              </label>

              <label className="si-span-2">
                <span className="si-label-row">Shopify API Key (Client ID) <span className="si-help" title="Found in the Shopify Dev / Partner dashboard. Required for OAuth installs.">?</span></span>
                <input
                  placeholder="App API key"
                  value={shopifyApiKey}
                  onChange={e => setShopifyApiKey(e.target.value)}
                />
              </label>

              <label className="si-span-2">
                <span className="si-label-row">Shopify API Secret <span className="si-help" title="App secret from the Shopify Dev / Partner dashboard. Required for OAuth installs.">?</span></span>
                <input
                  type="password"
                  placeholder="App secret"
                  value={shopifyApiSecret}
                  onChange={e => setShopifyApiSecret(e.target.value)}
                />
              </label>

              <div className="si-span-2" style={{ fontSize: 12, opacity: .8 }}>
                Provide either a legacy Admin API token or your app&apos;s API key & secret to finish OAuth setup.
              </div>
            </div>

            <StepActions />
          </div>
        )}

        {step === 3 && (
          <div>
            <StepHeader>Optional Settings</StepHeader>
            <div className="si-form-grid">
              <label>
                Store ID (optional)
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="e.g., 1234"
                  value={storeId}
                  onChange={e => setStoreId(e.target.value)}
                />
              </label>

              <label className="si-span-1">
                <span className="si-label-row">Fulfillment Location ID (optional) <span className="si-help" title='Shopify Fulfillment Location ID used when creating fulfillments.'>?</span></span>
                <input
                  placeholder="e.g., 654321"
                  value={fulfillmentLocationId}
                  onChange={e => setFulfillmentLocationId(e.target.value)}
                />
              </label>

              {/* NEW: Client selection (REQUIRED) */}
              <label className="si-span-2">
                <span className="si-label-row">Client <span className="si-help" title='Client that owns this integration and receives imported orders.'>?</span></span>
                <select
                  value={clientId}
                  onChange={e => {
                    const val = e.target.value;
                    setClientId(val);
                    const found = clients.find(c => String(c.id) === String(val));
                    setClientName(found?.name || '');
                  }}
                >
                  <option value="">— Select Client —</option>
                  {clients.map(c => (
                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                  ))}
                </select>
              </label>

              <div className="si-span-2" style={{ fontSize: 12, opacity: .8 }}>
                You can set Store ID and Fulfillment Location now or later from Edit. A client is required to finish.
              </div>

              {error && <div className="si-span-2 si-error" style={{ marginTop: 4 }}>{error}</div>}
            </div>

            <StepActions />
          </div>
        )}
      </div>
    </div>
  );
}

/* =================== Main View =================== */
export default function ShoppingIntegrations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [clients, setClients] = useState([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited or null for new

  // New Store wizard
  const [wizardOpen, setWizardOpen] = useState(false);

  // Test result modal
  const [testOpen, setTestOpen] = useState(false);
  const [testData, setTestData] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  // Sorting (persistent like Receipts)
  const initialSort = getInitialSort();
  const [sortField, setSortField] = useState(initialSort.sortField);
  const [sortOrder, setSortOrder] = useState(initialSort.sortOrder);

  useEffect(() => {
    try {
      localStorage.setItem(SORT_KEY, JSON.stringify({ sortField, sortOrder }));
    } catch {}
  }, [sortField, sortOrder]);

  useEffect(() => { load(); }, []);

  // Clients for dropdown (re-use existing /api/clients)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/clients?limit=1000&activeOnly=1`);
        const json = await res.json();
        const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
        setClients(list.map(c => ({ id: c.id, name: c.name })));
      } catch {
        setClients([]);
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/shoppingintegrations`);
      const json = await res.json();
      const data = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
      setRows(data.filter(r => Number(r.deleted || 0) === 0));
    } catch (e) {
      console.error('Failed to fetch shopping integrations', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const openNew   = () => { setEditing(null); setModalOpen(true); };
  const openEdit  = (row) => { setEditing(row); setModalOpen(true); };

  const saveIntegration = async (payload) => {
    try {
      if (payload.id) {
        const res = await fetch(`${API}/api/shoppingintegrations/${payload.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j?.error || 'Update failed');
        }
      } else {
        const res = await fetch(`${API}/api/shoppingintegrations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j?.error || 'Create failed');
        }
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const onDelete = async (row) => {
    const confirmText = window.prompt(
      `Type DELETE (all caps) to confirm deleting "${row.displayname || row.name}".`
    );
    if (confirmText !== 'DELETE') {
      alert('Delete cancelled — you must type DELETE exactly.');
      return;
    }
    try {
      const res = await fetch(`${API}/api/shoppingintegrations/${row.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || 'Delete failed');
      }
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const toggleEnabled = async (row, next) => {
    try {
      const res = await fetch(`${API}/api/shoppingintegrations/${row.id}/enabled`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !!next }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || 'Update failed');
      }
      setRows(prev => prev.map(r => (r.id === row.id ? { ...r, enabled: next ? 1 : 0 } : r)));
    } catch (e) {
      alert(e.message);
    }
  };

  const testConnection = async (row) => {
    try {
      // Special-case Amazon: show SP-API status endpoint (per integration)
      if ((row?.servicetype || '').toLowerCase().includes('amazon')) {
        const res = await fetch(`${API}/api/shopping/amazon/status?integrationId=${encodeURIComponent(row.id)}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Amazon status failed');
        setTestData({ provider: 'amazon', ...data });
        setTestOpen(true);
        return;
      }

      const res = await fetch(`${API}/api/shoppingintegrations/${row.id}/test`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Test failed');
      setTestData(data);
      setTestOpen(true);
    } catch (e) {
      alert(`Connection failed: ${e.message}`);
    }
  };

  const refreshAmazonToken = async (row) => {
    try {
      const res = await fetch(`${API}/api/shopping/amazon/auth/refresh?integrationId=${encodeURIComponent(row.id)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Refresh failed');
      setTestData({ provider: 'amazon', action: 'refresh', ...data });
      setTestOpen(true);
    } catch (e) {
      alert(`Refresh failed: ${e.message}`);
    }
  };

  const startShopifyOAuth = (row) => {
    if (!row?.id) return;
    const baseUrl = API_ROOT || '';
    const target = `${baseUrl}/api/shopify/integration/${row.id}/oauth/start`;
    startShopifyOauthFlow(target);
  };

  // Sorting helpers
  const handleSort = (field) => {
    if (sortField === field) setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    else { setSortField(field); setSortOrder('ASC'); }
  };

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const dir = sortOrder === 'ASC' ? 1 : -1;

    const getter = (r) => {
      switch (sortField) {
        case 'client':
          return (r.clientname || (r.clientid ? `#${r.clientid}` : '')).toString().toLowerCase();
        case 'integration':
          return (r.displayname || r.name || '').toString().toLowerCase();
        case 'servicetype':
          return (r.servicetype || '').toString().toLowerCase();
        case 'enabled':
          return Number(!!r.enabled);
        case 'storeid':
          return Number(r.storeid || 0);
        case 'name':
          return (r.name || '').toString().toLowerCase();
        case 'fulfillment_location_id':
          return (r.fulfillment_location_id ?? '').toString();
        default:
          return r[sortField] ?? '';
      }
    };

    arr.sort((a, b) => {
      const A = getter(a);
      const B = getter(b);
      if (A > B) return dir;
      if (A < B) return -dir;
      return 0;
    });
    return arr;
  }, [rows, sortField, sortOrder]);

  return (
    <div className="si-wrapper">
      <div className="si-header">
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn" onClick={() => setWizardOpen(true)}>
            <FaPlus /> New Store
          </button>
          <button className="btn primary" onClick={() => { setEditing(null); setModalOpen(true); }}>
            <FaPlus /> New (Advanced)
          </button>
        </div>
      </div>

      <div className="si-list-tablecol">
        {loading ? (
          <div className="loading">Loading…</div>
        ) : (
          <table className="si-table si-table--skinned">
            <thead>
              <tr>
                <th className="si-td-right">Actions</th>

                <th
                  onClick={() => handleSort('client')}
                  className={sortField === 'client' ? 'active-sort' : ''}
                >
                  Client {sortField === 'client' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                </th>

                <th
                  onClick={() => handleSort('integration')}
                  className={sortField === 'integration' ? 'active-sort' : ''}
                >
                  Integration {sortField === 'integration' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                </th>

                <th
                  onClick={() => handleSort('servicetype')}
                  className={sortField === 'servicetype' ? 'active-sort' : ''}
                >
                  Service {sortField === 'servicetype' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                </th>

                <th
                  onClick={() => handleSort('enabled')}
                  className={sortField === 'enabled' ? 'active-sort' : ''}
                >
                  Enabled {sortField === 'enabled' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                </th>

                <th
                  onClick={() => handleSort('storeid')}
                  className={sortField === 'storeid' ? 'active-sort' : ''}
                >
                  Store ID {sortField === 'storeid' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                </th>

                <th
                  onClick={() => handleSort('name')}
                  className={sortField === 'name' ? 'active-sort' : ''}
                >
                  Type {sortField === 'name' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                </th>

                <th
                  onClick={() => handleSort('fulfillment_location_id')}
                  className={sortField === 'fulfillment_location_id' ? 'active-sort' : ''}
                >
                  Fulfillment Location {sortField === 'fulfillment_location_id' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length ? sortedRows.map(r => {
                const svcName = String(r?.servicetype || '').toLowerCase();
                const isShopifyRow = svcName.includes('shopify');
                const rowShopifyMeta = isShopifyRow ? parseShopifyOauthMeta(r.field5) : null;
                const shopifyStatus = isShopifyRow
                  ? rowShopifyMeta
                    ? 'oauth'
                    : (r.field1 ? 'legacy' : 'none')
                  : null;
                return (
                <tr key={r.id}>
                  <td className="si-td-right">
                    <div className="actions-col">
                      <button className="mini-btn" type="button" title="Test Connection" onClick={() => testConnection(r)}>
                        <FaPlug size={16} />
                      </button>
                      {(r?.servicetype || '').toLowerCase().includes('amazon') && (
                        <button className="mini-btn" type="button" title="Amazon: Refresh LWA Token" onClick={() => refreshAmazonToken(r)}>
                          <FaPlug size={16} />
                        </button>
                      )}
                      {(r?.servicetype || '').toLowerCase().includes('shopify') && (
                        <button className="mini-btn" type="button" title="Shopify OAuth" onClick={() => startShopifyOAuth(r)}>
                          <FaKey size={16} />
                        </button>
                      )}
                      <button className="mini-btn" type="button" title="View" onClick={() => setViewRow(r)}>
                        <FaEye size={16} />
                      </button>
                      <button className="mini-btn" type="button" title="Edit" onClick={() => openEdit(r)}>
                        <FaEdit size={16} />
                      </button>
                      <button className="mini-btn danger" type="button" title="Delete" onClick={() => onDelete(r)}>
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </td>

                  <td className="si-nowrap">{r.clientname || (r.clientid ? `#${r.clientid}` : '')}</td>

                  <td className="si-nowrap">
                    <BrandIcon type={r.servicetype} /> {r.displayname || r.name}
                    {isShopifyRow && (
                      <span
                        className={`si-status-pill ${
                          shopifyStatus === 'oauth' ? 'success' : shopifyStatus === 'legacy' ? 'info' : 'muted'
                        }`}
                      >
                        {shopifyStatus === 'oauth' ? 'OAuth' : shopifyStatus === 'legacy' ? 'Legacy Token' : 'Needs OAuth'}
                      </span>
                    )}
                  </td>

                  <td>{r.servicetype}</td>

                  <td className="si-nowrap">
                    <label className="si-toggle">
                      <input
                        type="checkbox"
                        checked={!!r.enabled}
                        onChange={(e) => toggleEnabled(r, e.target.checked)}
                      />
                      Enabled
                    </label>
                  </td>

                  <td className="si-nowrap">{r.storeid || ''}</td>
                  <td className="si-nowrap">{r.name}</td>
                  <td className="si-nowrap">{r.fulfillment_location_id || ''}</td>
                </tr>
              )}) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', opacity: 0.7 }}>
                    No integrations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Connection Result Modal */}
      {testOpen && (
        <div className="si-modal-overlay" onClick={() => setTestOpen(false)}>
          <div className="si-modal" onClick={(e) => e.stopPropagation()}>
            <div className="si-modal-titlebar">
              <h3 className="si-modal-title">Connection Result</h3>
              <button className="icon-btn" aria-label="Close" onClick={() => setTestOpen(false)}>×</button>
            </div>

            {'shop' in (testData || {}) ? (
              <div className="si-test-summary">
                <div className="si-kv"><span className="k">Store:</span><span className="v">{testData.shop.name}</span></div>
                <div className="si-kv"><span className="k">Domain:</span><span className="v">{testData.shop.myshopify_domain || testData.shop.domain}</span></div>
                {'email' in testData.shop && (
                  <div className="si-kv"><span className="k">Email:</span><span className="v">{testData.shop.email}</span></div>
                )}
                {'plan_display_name' in testData.shop && (
                  <div className="si-kv"><span className="k">Plan:</span><span className="v">{testData.shop.plan_display_name}</span></div>
                )}
                {'created_at' in testData.shop && (
                  <div className="si-kv"><span className="k">Created:</span><span className="v">{new Date(testData.shop.created_at).toLocaleString()}</span></div>
                )}
                {'currency' in testData.shop && (
                  <div className="si-kv"><span className="k">Currency:</span><span className="v">{testData.shop.currency}</span></div>
                )}
              </div>
            ) : null}

            <div className="si-prewrap">
              <pre>{JSON.stringify(testData, null, 2)}</pre>
            </div>

            <div className="si-modal-actions">
              <button className="btn primary" onClick={() => setTestOpen(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal (advanced) */}
      <IntegrationModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={saveIntegration}
        initial={editing}
        clients={clients}
      />

      <IntegrationDetailsModal
        open={!!viewRow}
        row={viewRow}
        onClose={() => setViewRow(null)}
      />

      {/* New Store Wizard */}
      <NewStoreWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={load}
        clients={clients}
      />
    </div>
  );
}
